import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, X, Check, Reply, Trash2 } from 'lucide-react';

export function SimpleCommentSystem() {
  const location = useLocation();
  const [mode, setMode] = useState(false);
  const [comments, setComments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [username, setUsername] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    const user = localStorage.getItem('reviewer_username');
    if (user) setUsername(user);
    const data = localStorage.getItem('page_comments');
    if (data) {
      const all = JSON.parse(data);
      setComments(all[location.pathname] || []);
    }
  }, [location.pathname]);

  const save = (newComments) => {
    const data = localStorage.getItem('page_comments');
    const all = data ? JSON.parse(data) : {};
    all[location.pathname] = newComments;
    localStorage.setItem('page_comments', JSON.stringify(all));
    setComments(newComments);
  };

  const handleClick = (e) => {
    if (!mode) return;
    if (e.target.closest('.cmt-sys')) return;
    e.preventDefault();
    e.stopPropagation();
    if (!username) {
      setShowNameDialog(true);
      setMode(false);
      return;
    }
    const c = { id: Date.now(), x: e.clientX, y: e.clientY, text: '', author: username, time: Date.now(), replies: [], done: false };
    save([...comments, c]);
    setSelected(c);
    setMode(false);
  };

  useEffect(() => {
    if (mode) {
      document.addEventListener('click', handleClick);
      document.body.style.cursor = 'crosshair';
    } else {
      document.removeEventListener('click', handleClick);
      document.body.style.cursor = 'default';
    }
    return () => {
      document.removeEventListener('click', handleClick);
      document.body.style.cursor = 'default';
    };
  }, [mode, comments, username]);

  return (
    <>
      {comments.map(c => (
        <button key={c.id} onClick={() => setSelected(c)} className="cmt-sys fixed z-[9997] w-8 h-8 rounded-full shadow-lg flex items-center justify-center bg-accent border-2" style={{left: c.x+'px', top: c.y+'px', transform: 'translate(-50%, -50%)'}}>
          <MessageSquarePlus className="w-4 h-4" />
        </button>
      ))}
      <button onClick={() => setMode(!mode)} className="cmt-sys fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg z-[9998] bg-primary text-primary-foreground">
        {mode ? <X className="w-6 h-6" /> : <MessageSquarePlus className="w-6 h-6" />}
      </button>
      {showNameDialog && <NameDialog onSave={(n) => { localStorage.setItem('reviewer_username', n); setUsername(n); setShowNameDialog(false); }} onClose={() => setShowNameDialog(false)} name={name} setName={setName} />}
      {selected && <CommentView comment={selected} user={username} onClose={() => setSelected(null)} onSave={(text) => { const updated = comments.map(c => c.id === selected.id ? {...c, text} : c); save(updated); setSelected({...selected, text}); }} onDelete={() => { save(comments.filter(c => c.id !== selected.id)); setSelected(null); }} />}
    </>
  );
}

function NameDialog({onSave, onClose, name, setName}) {
  return (
    <div className="cmt-sys fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Enter your name</h2>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name or initials" className="w-full p-2 border rounded mb-4" />
        <div className="flex gap-2">
          <button onClick={() => name.trim() && onSave(name.trim())} className="px-4 py-2 bg-primary text-primary-foreground rounded">Start</button>
          <button onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CommentView({comment, user, onClose, onSave, onDelete}) {
  const [text, setText] = useState(comment.text);
  const [edit, setEdit] = useState(!comment.text);
  return (
    <div className="cmt-sys fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <h2 className="font-semibold">Comment</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-2 text-sm"><span className="font-semibold">{comment.author}</span> - {new Date(comment.time).toLocaleString()}</div>
        {edit ? (
          <div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4} className="w-full p-2 border rounded mb-2" />
            <button onClick={() => { if (text.trim()) { onSave(text); setEdit(false); }}} className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm">Save</button>
          </div>
        ) : (
          <div>
            <p className="mb-4">{comment.text}</p>
            {comment.author === user && <button onClick={() => setEdit(true)} className="text-sm text-muted-foreground">Edit</button>}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          {comment.author === user && <button onClick={onDelete} className="px-3 py-1 bg-destructive text-destructive-foreground rounded text-sm">Delete</button>}
        </div>
      </div>
    </div>
  );
}
